import React from 'react';
import { useNavigate } from 'react-router-dom';
import { X, AlertTriangle, Calendar, Package, ArrowRight } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { getStatusBadgeClass } from '../utils/statusColors';

interface Session {
  session_number: string;
  product_name?: string;
  product_sku?: string;
  scheduled_date?: string;
  status: string;
  station_name?: string;
  operator_name?: string;
  target_output_weight?: number;
  actual_output_weight?: number;
}

interface IncompleteSessionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  sessions: Session[];
}

const IncompleteSessionsModal: React.FC<IncompleteSessionsModalProps> = ({
  isOpen,
  onClose,
  sessions,
}) => {
  const { t } = useLanguage();
  const navigate = useNavigate();

  if (!isOpen) return null;

  const handleGoToSession = (scheduledDate: string) => {
    onClose();
    navigate(`/production-planning?date=${scheduledDate}`);
  };

  // Group sessions by date
  const sessionsByDate = sessions.reduce((acc: Record<string, Session[]>, session) => {
    const date = session.scheduled_date?.split('T')[0] || 'Unknown';
    if (!acc[date]) acc[date] = [];
    acc[date].push(session);
    return acc;
  }, {});

  // Sort dates descending (most recent first)
  const sortedDates = Object.keys(sessionsByDate).sort((a, b) => b.localeCompare(a));


  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Backdrop */}
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75 dark:bg-gray-900 dark:bg-opacity-75"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="inline-block w-full max-w-2xl my-8 overflow-hidden text-left align-middle transition-all transform bg-white dark:bg-gray-800 rounded-lg shadow-xl">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-amber-50 dark:bg-amber-900/20">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 dark:bg-amber-900/50 rounded-full">
                <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {t('supervisor.incompleteSessionsAlert')}
                </h3>
                <p className="text-sm text-amber-600 dark:text-amber-400">
                  {sessions.length} {t('supervisor.incompleteSessionsCount')}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
            {sortedDates.map((date) => (
              <div key={date} className="mb-6 last:mb-0">
                {/* Date Header */}
                <div className="flex items-center gap-2 mb-3">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    {date}
                  </h4>
                  <span className="text-xs text-gray-400 dark:text-gray-500">
                    ({sessionsByDate[date].length} session{sessionsByDate[date].length !== 1 ? 's' : ''})
                  </span>
                </div>

                {/* Sessions for this date */}
                <div className="space-y-2">
                  {sessionsByDate[date].map((session) => (
                    <div
                      key={session.session_number}
                      className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <Package className="w-4 h-4 text-gray-400" />
                            <span className="font-medium text-gray-900 dark:text-white">
                              {session.product_name || session.product_sku || 'Unknown Product'}
                            </span>
                            <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getStatusBadgeClass(session.status)}`}>
                              {t(`status.${session.status}`) || session.status}
                            </span>
                          </div>
                          {session.product_sku && session.product_name && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 ml-6">
                              {session.product_sku}
                            </p>
                          )}
                          <div className="flex items-center gap-4 mt-2 ml-6 text-xs text-gray-500 dark:text-gray-400">
                            {session.station_name && (
                              <span>Station: {session.station_name}</span>
                            )}
                            {session.operator_name && (
                              <span>Operator: {session.operator_name}</span>
                            )}
                            {session.target_output_weight && (
                              <span>
                                Progress: {Number(session.actual_output_weight || 0).toFixed(2)} / {Number(session.target_output_weight).toFixed(2)} lbs
                              </span>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={() => handleGoToSession(session.scheduled_date?.split('T')[0] || '')}
                          className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-amber-600 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded transition-colors"
                          title={t('supervisor.viewInPlanning')}
                        >
                          {t('common.view')}
                          <ArrowRight className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('supervisor.incompleteSessionsDescription')}
            </p>
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-500"
            >
              {t('common.close')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IncompleteSessionsModal;
